// src/components/ResendRegistrationLinkBanner.jsx (v3 – fully config-driven)

import React, { useState } from "react";
import api from "../services/api";
import { API_PATHS } from "../config";
import "../styles/ResendRegistrationLinkBanner.css";

/**
 * Banner allowing users to resend their registration/setup completion link.
 *
 * Shows success/error feedback and supports compact mode.
 *
 * @component
 * @param {Object} props
 * @param {string} props.email - Required: user's email address
 * @param {boolean} [props.compact=false] - Use tighter layout
 * @param {function} [props.onSent] - Callback on success (receives message)
 * @param {function} [props.onError] - Callback on failure (receives error message)
 * @param {string} [props.className=""] - Additional wrapper classes
 * @returns {JSX.Element}
 */
const ResendRegistrationLinkBanner = ({
  email,
  compact = false,
  onSent,
  onError,
  className = "",
}) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const resend = async () => {
    if (!email) return;

    setBusy(true);
    setMsg("");
    setErr("");

    try {
      const { data } = await api.post(API_PATHS.RESEND_REGISTRATION, { email });

      const text =
        data?.message || "A new completion link has been sent to your email.";
      setMsg(text);
      onSent?.(text);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || "Failed to resend the setup link.";
      setErr(detail);
      onError?.(detail);
    } finally {
      setBusy(false);
    }
  };

  const rootClasses = [
    "gg-resend-banner",
    compact && "gg-resend-banner--compact",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const buttonClasses = [
    "gg-resend-banner__button",
    compact && "gg-resend-banner__button--compact",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClasses}>
      <div className="gg-resend-banner__row">
        <span className="gg-resend-banner__text">
          Can’t use the link? We can send a fresh setup link to{" "}
          <strong>{email || "your email"}</strong>.
        </span>
        <button
          type="button"
          onClick={resend}
          disabled={busy || !email}
          className={buttonClasses}
        >
          {busy ? "Sending…" : "Resend setup link"}
        </button>
      </div>

      {msg && (
        <div
          className="gg-resend-banner__message gg-resend-banner__message--success"
          role="status"
          aria-live="polite"
        >
          {msg}
        </div>
      )}
      {err && (
        <div
          className="gg-resend-banner__message gg-resend-banner__message--error"
          role="alert"
        >
          {err}
        </div>
      )}
    </div>
  );
};

export default ResendRegistrationLinkBanner;